import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner, faWarehouse, faBox, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
// Receive inventories from parent to avoid creating separate hook instance
import validateUtils from '../../utils/validate';
import CoilSplitPanel from '../coils/CoilSplitPanel';
import warehouseService from '../../services/warehouseService';

const WarehouseInventoryEditForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  productSizeId = null,
  loading = false,
  inventories = [],
  inventoryLoading = false,
}) => {
  const { t } = useTranslation();
  const strings = t('warehouseInventory.forms', { returnObjects: true });
  const detailStrings = t('productSizes.distribution', { returnObjects: true });
  const tableStrings = t('warehouseInventory.table.columns', { returnObjects: true });
  const REQUIRED = t('common.form.requiredIndicator');
  
  // `inventories` and `inventoryLoading` are received from the parent component
  
  // State for product data and warehouse updates
  const [productData, setProductData] = useState(null);
  const [warehouseUpdates, setWarehouseUpdates] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  // Every warehouse, not just the ones this product already has a row in — a
  // product can only be stocked somewhere new if somewhere new can be chosen.
  const [allWarehouses, setAllWarehouses] = useState([]);
  const [warehouseToAdd, setWarehouseToAdd] = useState('');

  // Load product distribution from local inventory data
  const loadProductDistribution = useCallback((prodId) => {
    if (!prodId) return;
    setDataLoading(true);
    try {
      // Find the product inventory record with warehouse breakdown
      // Use string coercion for resilient matching (handles numeric vs string ids)
      const productInventory = inventories.find(inv => String(inv.product_size_id) === String(prodId));
      
      if (productInventory) {
        setProductData(productInventory);
        
        // Initialize warehouse updates from the product's warehouse breakdown
        if (productInventory.warehouse_breakdown && Array.isArray(productInventory.warehouse_breakdown)) {
          const updates = productInventory.warehouse_breakdown.map((warehouse) => ({
            warehouse_id: warehouse.warehouse_id || warehouse.id,
            warehouse_name: warehouse.warehouse_name || warehouse.name || `Warehouse ${warehouse.warehouse_id || warehouse.id}`,
            stock_quantity: String(warehouse.stock_quantity ?? warehouse.available_inventory ?? 0),
            minimum_stock: String(warehouse.minimum_stock ?? 0),
            pending_order: warehouse.pending_order ?? 0,
            is_low_stock: warehouse.is_low_stock || false,
          }));
          setWarehouseUpdates(updates);
        } else {
          setWarehouseUpdates([]);
        }
      } else {
        // Aggregate individual inventory records for this product
        const productInventories = inventories.filter(inv => String(inv.product_size_id) === String(prodId));
        
        if (productInventories.length > 0) {
          // Use first record for product info, build warehouse breakdown from all records
          const firstRecord = productInventories[0];
          const warehouseBreakdown = productInventories.map(inv => ({
            warehouse_id: inv.warehouse_id,
            warehouse_name: inv.warehouse_details?.name || inv.warehouse_name || `Warehouse ${inv.warehouse_id}`,
            stock_quantity: inv.stock_quantity ?? 0,
            minimum_stock: inv.minimum_stock ?? 0,
            pending_order: inv.pending_order ?? 0,
            is_low_stock: inv.is_low_stock || false,
          }));
          
          // Calculate totals
          const totalStock = productInventories.reduce((sum, inv) => sum + (inv.stock_quantity || 0), 0);
          const totalPending = productInventories.reduce((sum, inv) => sum + (inv.pending_order || 0), 0);
          const totalAvailable = productInventories.reduce((sum, inv) => sum + ((inv.stock_quantity || 0) - (inv.pending_order || 0)), 0);
          
          const aggregatedProductData = {
            ...firstRecord,
            warehouse_breakdown: warehouseBreakdown,
            total_stock_all_warehouses: totalStock,
            total_pending_all_warehouses: totalPending,
            total_available_all_warehouses: totalAvailable,
            warehouse_count: productInventories.length,
          };
          
          setProductData(aggregatedProductData);
          
          const updates = warehouseBreakdown.map((warehouse) => ({
            warehouse_id: warehouse.warehouse_id,
            warehouse_name: warehouse.warehouse_name,
            stock_quantity: String(warehouse.stock_quantity),
            minimum_stock: String(warehouse.minimum_stock),
            pending_order: warehouse.pending_order,
            is_low_stock: warehouse.is_low_stock,
          }));
          setWarehouseUpdates(updates);
        } else {
          // No data found for this product
          setProductData(null);
          setWarehouseUpdates([]);
        }
      }
    } catch (error) {
      console.error('Error loading product distribution:', error);
    } finally {
      setDataLoading(false);
    }
  }, [inventories]);

  // Load data when modal opens or productSizeId changes
  useEffect(() => {
    if (isOpen && productSizeId) {
      loadProductDistribution(productSizeId);
    }
  }, [isOpen, productSizeId, loadProductDistribution]);

  // Reuses the cache WarehouseInventoryForm already populates, so opening this
  // modal after that one costs no request. Failure leaves the existing rows fully
  // editable — it only removes the ability to add a new warehouse.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const cached = window?.warehouseInventoryFormCache?.warehouses;
    if (Array.isArray(cached) && cached.length > 0) {
      setAllWarehouses(cached);
      return undefined;
    }
    warehouseService
      .getAllWarehouses()
      .then((result) => {
        if (cancelled) return;
        const rows = result?.success && Array.isArray(result.data) ? result.data : [];
        setAllWarehouses(rows.map((w) => ({ value: w.id, label: w.name || `Warehouse ${w.id}` })));
      })
      .catch(() => { if (!cancelled) setAllWarehouses([]); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setProductData(null);
      setWarehouseUpdates([]);
      setErrors({});
      setSubmitting(false);
      setWarehouseToAdd('');
    }
  }, [isOpen]);

  const handleWarehouseChange = (index, field, value) => {
    setWarehouseUpdates(prev => {
      const updated = [...prev];
      if (field === 'stock_quantity' || field === 'minimum_stock') {
        updated[index] = { ...updated[index], [field]: validateUtils.inputFilters.numeric(value) };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
    
    // Clear error for this field
    if (errors[`warehouse.${index}.${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`warehouse.${index}.${field}`];
        return newErrors;
      });
    }
  };

  const isCoilTrackedProduct = Boolean(productData?.coil_tracked);
  // Stock for this size is a piece count, not kg — label the stock input "pcs".
  const isPiecesTrackedProduct = Boolean(productData?.track_by_pieces);

  const handleAddWarehouse = () => {
    if (!warehouseToAdd) return;
    const chosen = allWarehouses.find((w) => String(w.value) === String(warehouseToAdd));
    if (!chosen) return;
    setWarehouseUpdates((prev) => [
      ...prev,
      {
        warehouse_id: chosen.value,
        warehouse_name: chosen.label,
        // A coil-tracked size's stock arrives by receiving a coil, never by typing
        // here, so the new row starts at the only value the backend will accept.
        stock_quantity: isCoilTrackedProduct ? '0' : '',
        minimum_stock: '0',
        pending_order: 0,
        is_low_stock: false,
        is_new: true,
      },
    ]);
    setWarehouseToAdd('');
  };

  const handleRemoveWarehouse = (index) => {
    setWarehouseUpdates((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (!key.startsWith(`warehouse.${index}.`)) next[key] = prev[key];
      });
      return next;
    });
  };

  const validate = () => {
    const v = {};
    
    if (!productSizeId) {
      v.product = 'Product is required';
    }
    
    if (!warehouseUpdates || warehouseUpdates.length === 0) {
      v.warehouses = 'At least one warehouse must be configured';
    } else {
      warehouseUpdates.forEach((warehouse, index) => {
        // Stock quantity validation. Skipped for a coil-tracked size: the field
        // is not editable there, and its value is echoed back unchanged.
        if (!isCoilTrackedProduct) {
          if (!warehouse.stock_quantity || String(warehouse.stock_quantity).trim() === '') {
            v[`warehouse.${index}.stock_quantity`] = 'Stock quantity is required';
          } else if (isNaN(Number(warehouse.stock_quantity)) || Number(warehouse.stock_quantity) < 0) {
            v[`warehouse.${index}.stock_quantity`] = 'Enter a valid stock quantity';
          }
        }
        
        // Minimum stock validation
        if (!warehouse.minimum_stock || String(warehouse.minimum_stock).trim() === '') {
          v[`warehouse.${index}.minimum_stock`] = 'Minimum stock is required';
        } else if (isNaN(Number(warehouse.minimum_stock)) || Number(warehouse.minimum_stock) < 0) {
          v[`warehouse.${index}.minimum_stock`] = 'Enter a valid minimum stock';
        }
      });
    }
    
    setErrors(v);
    return Object.keys(v).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        product_size_id: parseInt(productSizeId, 10),
        warehouse_updates: warehouseUpdates.map(warehouse => ({
          warehouse_id: parseInt(warehouse.warehouse_id, 10),
          stock_quantity: warehouse.stock_quantity === '' ? 0 : parseFloat(warehouse.stock_quantity),
          minimum_stock: warehouse.minimum_stock === '' ? 0 : parseFloat(warehouse.minimum_stock),
        })),
      };
      
      // Close only on success — keep the entered values on a backend rejection.
      const res = await onSubmit(payload);
      if (!res || res.success) onClose();
    } catch (error) {
      console.error('Error submitting warehouse updates:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const scrollClass = 'warehouse-inventory-edit-form-scroll';

  // For a coil-tracked size the balance is not an independent number — it is the
  // sum of that size's coils, and the backend refuses any posting that moves it
  // from here. So show what it is a sum OF, and don't offer an input that would
  // only be rejected. Minimum stock is not stock, so it stays editable.
  const isCoilTracked = isCoilTrackedProduct;

  const usedWarehouseIds = new Set(warehouseUpdates.map((w) => String(w.warehouse_id)));
  const availableWarehouses = allWarehouses.filter(
    (w) => !usedWarehouseIds.has(String(w.value))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <style>{`
        .${scrollClass} { -ms-overflow-style: none; scrollbar-width: none; }
        .${scrollClass}::-webkit-scrollbar { display: none; }
      `}</style>
      <div className={`w-full max-w-4xl relative bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${scrollClass}`} style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faBox} className="text-2xl" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
            <div>
              <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {detailStrings.header.sectionTitle}
              </h2>
              <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                Edit warehouse distribution for product
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 rounded-lg hover:opacity-75 transition-opacity" 
            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Loading overlay */}
          {submitting && (
            <div className="absolute inset-0 z-40 flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  Updating inventory...
                </p>
              </div>
            </div>
          )}

          {/* Product Information */}
          {(dataLoading || inventoryLoading) ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-3xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p style={{ color: UI_COLORS.TEXT.SECONDARY }}>Loading product distribution...</p>
              </div>
            </div>
          ) : productData ? (
            <>
              {/* Product Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  Product Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                      {detailStrings.fields.productName}
                    </p>
                    <p className="text-lg" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                      {productData.product_details?.alias_name || productData.product_name || productData.product_details?.product_size || 'Unknown Product'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                      {detailStrings.fields.productSize}
                    </p>
                    <p className="text-lg" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                      {productData.product_details?.product_size || productData.product_size || t('common.symbols.emDash')}
                    </p>
                  </div>
                </div>

                {/* Summary Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                  <div className="text-center">
                    <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                      {tableStrings.total_stock_all_warehouses}
                    </p>
                    <p className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                      {productData.total_stock_all_warehouses ?? t('common.symbols.emDash')}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                      {tableStrings.total_pending_all_warehouses}
                    </p>
                    <p className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                      {productData.total_pending_all_warehouses ?? t('common.symbols.emDash')}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                      {tableStrings.total_available_all_warehouses}
                    </p>
                    <p className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                      {productData.total_available_all_warehouses ?? t('common.symbols.emDash')}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                      {tableStrings.warehouse_count}
                    </p>
                    <p className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                      {productData.warehouse_count ?? warehouseUpdates.length}
                    </p>
                  </div>
                </div>
              </div>

              {/* Warehouse Distribution Form */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faWarehouse} style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                  <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    Warehouse Distribution
                  </h3>
                </div>

                {errors.warehouses && (
                  <div className="text-sm mb-4 p-3 rounded-lg bg-red-50" style={{ color: UI_COLORS.STATUS.ERROR }}>
                    {errors.warehouses}
                  </div>
                )}

                <div className="space-y-4">
                  {warehouseUpdates.map((warehouse, index) => (
                    <div key={`${warehouse.warehouse_id}-${index}`} className="border rounded-xl p-4" style={{ borderColor: warehouse.is_low_stock ? UI_COLORS.STATUS.WARNING : UI_COLORS.BORDERS.LIGHT }}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon icon={faWarehouse} className="text-lg" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                          <div>
                            <h4 className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                              {warehouse.warehouse_name}
                            </h4>
                            {warehouse.is_low_stock && (
                              <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: UI_COLORS.STATUS.WARNING, color: 'white' }}>
                                Low Stock
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                            Pending: {warehouse.pending_order || 0}
                          </div>
                          {/* Only a row added in this session can be dropped. Removing an
                              existing row is not what this endpoint does — it would leave
                              the stock behind, silently. */}
                          {warehouse.is_new && (
                            <button
                              type="button"
                              onClick={() => handleRemoveWarehouse(index)}
                              className="p-2 rounded-lg hover:opacity-75 transition-opacity"
                              style={{
                                color: UI_COLORS.STATUS.ERROR,
                                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
                              }}
                              title={t('warehouseInventory.forms.addWarehouse.remove')}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Stock Quantity */}
                        <div>
                          <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {strings.fields.stock_quantity.label}{isPiecesTrackedProduct ? ` (${t('pieces.inventory.unitPieces')})` : ''} {isCoilTracked ? null : REQUIRED}
                          </label>
                          {isCoilTracked ? (
                            <div
                              className="w-full px-4 py-3 rounded-xl border-2"
                              style={{
                                backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.06),
                                borderColor: UI_COLORS.BORDERS.LIGHT,
                                color: UI_COLORS.TEXT.PRIMARY,
                              }}
                            >
                              {warehouse.stock_quantity}
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={warehouse.stock_quantity}
                              onChange={(e) => handleWarehouseChange(index, 'stock_quantity', e.target.value)}
                              className="w-full px-4 py-3 rounded-xl border-2"
                              placeholder="Enter stock quantity"
                              style={{
                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                borderColor: errors[`warehouse.${index}.stock_quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                                color: UI_COLORS.FORMS.INPUT.text,
                              }}
                            />
                          )}
                          {errors[`warehouse.${index}.stock_quantity`] && (
                            <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                              {errors[`warehouse.${index}.stock_quantity`]}
                            </div>
                          )}
                        </div>

                        {/* Minimum Stock */}
                        <div>
                          <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {strings.fields.minimum_stock.label} {REQUIRED}
                          </label>
                          <input
                            type="text"
                            value={warehouse.minimum_stock}
                            onChange={(e) => handleWarehouseChange(index, 'minimum_stock', e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border-2"
                            placeholder="Enter minimum stock"
                            style={{
                              backgroundColor: UI_COLORS.FORMS.INPUT.background,
                              borderColor: errors[`warehouse.${index}.minimum_stock`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                              color: UI_COLORS.FORMS.INPUT.text,
                            }}
                          />
                          {errors[`warehouse.${index}.minimum_stock`] && (
                            <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                              {errors[`warehouse.${index}.minimum_stock`]}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* The quantity above is a sum for a coil-tracked size; this is
                          what it is a sum OF, so the operator can see which rolls make
                          it up before changing anything else on the row. */}
                      {isCoilTracked && (
                        <>
                          <p className="text-xs mt-3" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                            {t('coils.ledger.messages.stockManagedByCoils')}
                          </p>
                          <CoilSplitPanel
                            productSizeId={productSizeId}
                            warehouseId={warehouse.warehouse_id}
                            expectedKg={Number(warehouse.stock_quantity)}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {warehouseUpdates.length === 0 && (
                  <div className="text-center py-8">
                    <FontAwesomeIcon icon={faWarehouse} className="text-4xl mb-4" style={{ color: UI_COLORS.TEXT.MUTED }} />
                    <p style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                      No warehouse distribution found for this product.
                    </p>
                  </div>
                )}

                {/* Stock this product in a warehouse it isn't stocked in yet. The
                    backend creates the missing inventory row, so the only thing
                    that was ever needed here was a way to name the warehouse. */}
                {availableWarehouses.length > 0 && (
                  <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center">
                    <select
                      value={warehouseToAdd}
                      onChange={(e) => setWarehouseToAdd(e.target.value)}
                      className="flex-1 px-4 py-3 rounded-xl border-2"
                      style={{
                        backgroundColor: UI_COLORS.FORMS.INPUT.background,
                        borderColor: UI_COLORS.FORMS.INPUT.border,
                        color: UI_COLORS.FORMS.INPUT.text,
                      }}
                    >
                      <option value="">{t('warehouseInventory.forms.addWarehouse.placeholder')}</option>
                      {availableWarehouses.map((w) => (
                        <option key={w.value} value={w.value}>{w.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddWarehouse}
                      disabled={!warehouseToAdd}
                      className="px-6 py-3 rounded-xl font-semibold disabled:opacity-50"
                      style={{
                        backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
                        color: UI_COLORS.BUTTONS.SECONDARY.text,
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      {t('warehouseInventory.forms.addWarehouse.button')}
                    </button>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-6 mt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <button 
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 rounded-xl font-semibold"
                  style={{ 
                    backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, 
                    color: UI_COLORS.BUTTONS.SECONDARY.text 
                  }}
                >
                  {strings.buttons.cancel}
                </button>
                <button 
                  type="submit" 
                  disabled={loading || submitting || warehouseUpdates.length === 0}
                  className="px-6 py-3 rounded-xl font-semibold disabled:opacity-50"
                  style={{ 
                    backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, 
                    color: UI_COLORS.BUTTONS.PRIMARY.text 
                  }}
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" /> 
                  Update Distribution
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                Product data not found. Please try again.
              </p>
                {/* removed debug info */}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default WarehouseInventoryEditForm;